import {Injectable} from '@angular/core';
import {map, takeUntil} from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { TowerGroupDto } from 'src/app/api/models';
import { RoutingInfo } from 'src/shared/models/global.model';

@Injectable({
	providedIn: 'root'
})
export class GeolocationService {
    private groupsObs$:  BehaviorSubject<Array<TowerGroupDto>> = new BehaviorSubject(null);
	private routingObs$: BehaviorSubject<RoutingInfo> = new BehaviorSubject(null);
	constructor() { }

	getCurrentPosition() {
		const options = {
			timeout: undefined,
			enableHighAccuracy: true,
			maximumAge: 300000,
		};
		return new Observable(observer => {
			if(window.navigator && window.navigator.geolocation) {
				window.navigator.geolocation.getCurrentPosition(
					(position) => {
						observer.next(position);
						observer.complete();
					},
					(error) => {observer.error(error), this.handleError()},
					options
				);
			} else {
				observer.error('Unsupported Browser');
			}
		})  as any;
	}

	handleError() {
		if (window.navigator.permissions && window.navigator.permissions.query) {
			//try permissions APIs first
			window.navigator.permissions.query({ name: 'geolocation' }).then((result) => {
				// Will return ['granted', 'prompt', 'denied']
				const permission = result.state;
				if ( permission === 'granted' || permission === 'prompt' ) {
					this.getCurrentPosition();
				}
			});
		  } else if (window.navigator.geolocation) {
			//then Navigation APIs
			this.getCurrentPosition();
		  }
	}

	watchPosition() {
		return new Observable(observer => {
			if(window.navigator && window.navigator.geolocation) {
				window.navigator.geolocation.watchPosition(
					(position) => {
						observer.next(position);
						observer.complete();
					},
					(error) => {
						observer.error(error),
						window.navigator.geolocation.clearWatch(1),
						this.watchPosition()
					},
					{timeout: undefined, maximumAge: 300000, enableHighAccuracy: true}
				);
			} else {
				observer.error('Unsupported Browser');
			}
		}) as any;
	}

	calculateDistanceForList(groups: Array<TowerGroupDto>, onComplete) {
		return this.getCurrentPosition()
			.pipe(
				map(userCoords => {
					groups.map(g => {
						g['distance'] = this.calcDistanceWithUserLocation(g.geoLat, g.geoLng, userCoords);
					});

					groups.sort((a, b) => {
						const dis1 = a['distance'].d;
						const dis2 = b['distance'].d;
						return dis1 - dis2;
					});
					this.setGroupsObs(groups);
					onComplete();
				})
			);
	}

	calculateDistance(groupLat1, groupLon1) {
		return this.getCurrentPosition()
			.pipe(
				map(userCoords => {
					return this.calcDistanceWithUserLocation(groupLat1, groupLon1, userCoords);
				})
			);
	}

	getGroupsObs(): Observable<TowerGroupDto[]> {
        return this.groupsObs$.asObservable();
    }

    setGroupsObs(groups: TowerGroupDto[]) {
        this.groupsObs$.next(groups);
    }

	getRoutingObs(): Observable<RoutingInfo> {
        return this.routingObs$.asObservable();
    }

    setRoutingObs(routingInfo: RoutingInfo) {
        this.routingObs$.next(routingInfo);
    }

	calcDistanceWithUserLocation(groupLat1, groupLon1, userCoords) {
		const R = 6371; // km
		const x1 = userCoords?.coords?.latitude - groupLat1;
		const dLat = this.toRad(x1);
		const x2 = userCoords?.coords?.longitude - groupLon1;
		const dLon = this.toRad(x2);
		const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(this.toRad(groupLat1)) * Math.cos(this.toRad(userCoords?.coords?.latitude)) *
			Math.sin(dLon / 2) * Math.sin(dLon / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		const d = R * c;
		return {
			d: +(d * 1000).toFixed(0),
			dStr: this.convertDistance(d)
		};
	}

	private toRad(val) {
		return val * Math.PI / 180;
	}

	private convertDistance(distance: number) {
		let result = '';
		let km = '';
		let m = '';

		if (distance >= 1) {
			km = `${parseFloat(distance.toFixed(1))} km`;
		}
		else {
			m = `${((distance - Math.floor(distance)) * 1000).toFixed(0)} m`;
		}

		result = `${km} ${m}`;
		return result;
	}
}
